import React, { useCallback, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import ModalImage from "~components/Modal/ModalImage";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  LinearScale,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, ChartDataLabels);

const truncar = (nome, max = 16) =>
  nome.length > max ? `${nome.substring(0, max)}...` : nome;


const rotuloCliente = (item) =>
  item?.codigo ? `${item.codigo}${" - "}${item.cliente}` : String(item?.cliente ?? "");


const truncarRotulo = (rotulo, max) => {
  const corte = rotulo.indexOf(" - ");
  if (corte === -1) return truncar(rotulo, max);
  const prefixo = rotulo.slice(0, corte + " - ".length);
  return prefixo + truncar(rotulo.slice(prefixo.length), Math.max(8, max - prefixo.length));
};

const fmtConsumo = (valor) =>
  valor == null
    ? "—"
    : new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor);

const maxCaracteres = (larguraChart) => {
  if (larguraChart < 380) return 12;
  if (larguraChart < 560) return 18;
  return 24;
};

const tickCallback = function tick(value) {
  const rotulo = String(this.getLabelForValue(value) ?? "");
  return truncarRotulo(rotulo, maxCaracteres(this.chart.width));
};

const tooltipConfig = {
  enabled: true,
  displayColors: false,
  callbacks: {
    label: (ctx) => (ctx.parsed.x != null ? ctx.parsed.x.toFixed(2) : ""),
  },
};

const interactionConfig = { mode: "nearest", axis: "y", intersect: false };

const handleHover = (evt, elements) => {
  const canvas = evt.native?.target;
  if (canvas) canvas.style.cursor = elements.length ? "pointer" : "default";
};

const tickInteractionPlugin = {
  id: "tickInteraction",
  afterEvent(chart, args) {
    const { event, inChartArea } = args;
    if (inChartArea) return;

    const yScale = chart.scales.y;
    const { tooltip } = chart;
    if (!yScale || !tooltip) return;

    const sobreRotulos =
      event.type !== "mouseout" &&
      event.x >= yScale.left &&
      event.x <= yScale.right &&
      event.y >= yScale.top &&
      event.y <= yScale.bottom;

    if (!sobreRotulos) {
      chart.canvas.style.cursor = "default";
      if (tooltip.getActiveElements().length) {
        tooltip.setActiveElements([], {});
        args.changed = true;
      }
      return;
    }

    const barras = chart.getDatasetMeta(0)?.data ?? [];
    if (!barras.length) return;

    let index = 0;
    let menorDist = Infinity;
    barras.forEach((barra, i) => {
      const dist = Math.abs(barra.y - event.y);
      if (dist < menorDist) {
        menorDist = dist;
        index = i;
      }
    });

    chart.canvas.style.cursor = "pointer";

    if (event.type === "click") {
      chart.options.onClick?.(event, [{ datasetIndex: 0, index }], chart);
      return;
    }

    tooltip.setActiveElements([{ datasetIndex: 0, index }], { x: event.x, y: event.y });
    args.changed = true;
  },
};

const fullWidthLinePlugin = {
  id: "fullWidthLine",
  afterDraw(chart) {
    const { ctx, chartArea } = chart;
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(0, chartArea.bottom);
    ctx.lineTo(chart.width, chartArea.bottom);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 0.1;
    ctx.stroke();
    ctx.restore();
  },
};

const leftOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  interaction: interactionConfig,
  onHover: handleHover,
  layout: { padding: { right: 0, top: 4, bottom: 0, left: 0 } },
  plugins: {
    legend: { display: false },
    tooltip: tooltipConfig,
    datalabels: {
      anchor: "end",
      align: "start",
      color: "#fff",
      font: { size: 11, weight: "bold" },
      formatter: (v) => (v != null ? v.toFixed(2) : ""),
    },
  },
  scales: {
    x: {
      max: 0,
      grid: { display: false },
      border: { display: false },
      ticks: {
        font: { size: 10 },
        color: "#888",
        callback: (v) => v.toFixed(2),
      },
    },
    y: {
      position: "left",
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 11 }, color: "#1A2B49", callback: tickCallback, crossAlign: "far" },
    },
  },
};

const rightOptions = {
  indexAxis: "y",
  responsive: true,
  maintainAspectRatio: false,
  interaction: interactionConfig,
  onHover: handleHover,
  layout: { padding: { left: 0, top: 4, bottom: 0, right: 0 } },
  plugins: {
    legend: { display: false },
    tooltip: tooltipConfig,
    datalabels: {
      anchor: "start",
      align: "end",
      color: "#fff",
      font: { size: 11, weight: "bold" },
      formatter: (v) => (v != null ? v.toFixed(2) : ""),
    },
  },
  scales: {
    x: {
      min: 0,
      grid: { display: false },
      border: { display: false },
      ticks: {
        font: { size: 10 },
        color: "#888",
        callback: (v) => v.toFixed(2),
      },
    },
    y: {
      position: "right",
      grid: { display: false },
      border: { display: false },
      ticks: { font: { size: 11 }, color: "#1A2B49", callback: tickCallback, crossAlign: "near" },
    },
  },
};

const WinLossChart = ({
  title = "WinLoss por Cliente",
  diminuiram = [],
  aumentaram = [],
  nota = "*Comparando o consumo de volume mapeado em pesquisas",
  emptyMessage,
}) => {
  const [selecionado, setSelecionado] = useState(null);

  const fecharModal = useCallback(() => setSelecionado(null), []);

  const abrirModal = useCallback((lista, situacao) => (evt, elements) => {
    const index = elements?.[0]?.index;
    const item = index != null ? lista[index] : null;
    if (item) setSelecionado({ ...item, situacao });
  }, []);

  const leftChartOptions = useMemo(
    () => ({ ...leftOptions, onClick: abrirModal(diminuiram, "diminuiu") }),
    [abrirModal, diminuiram]
  );

  const rightChartOptions = useMemo(
    () => ({ ...rightOptions, onClick: abrirModal(aumentaram, "aumentou") }),
    [abrirModal, aumentaram]
  );

  const leftChartData = {
    labels: diminuiram.map(rotuloCliente),
    datasets: [
      {
        data: diminuiram.map((d) => d.valor),
        backgroundColor: "#E53935",
        borderRadius: 4,
        barPercentage: 0.65,
        categoryPercentage: 0.85,
      },
    ],
  };

  const rightChartData = {
    labels: aumentaram.map(rotuloCliente),
    datasets: [
      {
        data: aumentaram.map((d) => d.valor),
        backgroundColor: "#37A800",
        borderRadius: 4,
        barPercentage: 0.65,
        categoryPercentage: 0.85,
      },
    ],
  };

  return (
    <div className="overflow-hidden rounded-xl bg-white" style={{ boxShadow: "0 1px 6px 0 rgba(0,0,0,0.10)" }}>
      <div className="bg-[#01054C] px-6 py-3 text-center">
        <h2 className="text-base font-bold text-white">{title}</h2>
      </div>

      {emptyMessage ? (
        <div style={{ height: 200 }} className="flex items-center justify-center px-6">
          <p className="text-center text-sm font-semibold text-[#8D96A8]">{emptyMessage}</p>
        </div>
      ) : (
      <div className="flex flex-col md:flex-row md:divide-x divide-gray-100 px-4 pt-4 pb-2">
        <div className="flex-1 md:pr-4 pb-4 md:pb-0 border-b border-gray-100 md:border-b-0">
          <p className="mb-3 text-sm font-semibold text-[#1A2B49]">Clientes que diminuíram consumo</p>
          <div style={{ height: 280 }}>
            <Bar
              data={leftChartData}
              options={leftChartOptions}
              plugins={[fullWidthLinePlugin, tickInteractionPlugin]}
            />
          </div>
          <p className="mt-2 text-[11px] italic text-gray-400">{nota}</p>
        </div>

        <div className="flex-1 md:pl-4 pt-4 md:pt-0">
          <p className="mb-3 text-sm font-semibold text-[#1A2B49]">Clientes que aumentaram consumo</p>
          <div style={{ height: 280 }}>
            <Bar
              data={rightChartData}
              options={rightChartOptions}
              plugins={[fullWidthLinePlugin, tickInteractionPlugin]}
            />
          </div>
          <p className="mt-2 text-[11px] italic text-gray-400">{nota}</p>
        </div>
      </div>
      )}

      <ModalImage isOpen={!!selecionado} closeModal={fecharModal} childrenClass="text-left">
        {selecionado && (
          <>
            <span
              className="inline-block rounded-full px-3 py-1 text-[11px] font-bold uppercase text-white"
              style={{ backgroundColor: selecionado.situacao === "diminuiu" ? "#E53935" : "#37A800" }}
            >
              {selecionado.situacao === "diminuiu" ? "Diminuiu consumo" : "Aumentou consumo"}
            </span>

            <h3 className="mt-3 break-words text-xl font-bold leading-snug text-[#1A2B49]">
              {selecionado.cliente}
            </h3>

            <dl className="mt-4 text-sm">
              <div className="flex items-center justify-between gap-6 border-b border-gray-100 py-2">
                <dt className="text-gray-500">Código do cliente</dt>
                <dd className="font-semibold text-[#1A2B49]">{selecionado.codigo ?? "—"}</dd>
              </div>
              <div className="flex items-center justify-between gap-6 py-2">
                <dt className="text-gray-500">Consumo</dt>
                <dd
                  className="font-semibold"
                  style={{ color: selecionado.situacao === "diminuiu" ? "#E53935" : "#37A800" }}
                >
                  {fmtConsumo(selecionado.valor)}
                </dd>
              </div>
            </dl>

            <p className="mt-4 text-[11px] italic text-gray-400">{nota}</p>
          </>
        )}
      </ModalImage>
    </div>
  );
};

export default WinLossChart;
